import { PlusOutlined, DownloadOutlined } from '@ant-design/icons';
import { Space, message, Tabs } from 'antd';
import { useState } from 'react';

import { Button } from '../../../../components/antd/Button';
import { useChangePagination } from '../../../../hooks';
import {
  useGetCargosQuery,
  useGetProductGroupsQuery,
  useGetCargoTypeQuery,
} from '../../../../services/cargoApi';
import { useLazyExportRegistersQuery } from '../../../../services/exportApi';
import {
  Cargo as CargoType,
  ProductGroup,
  CargoType as CargoTypeType,
} from '../../../../types';
import { PanelHeader } from '../panelHeader';
import { RegistersDrawer } from '../registersDrawer';
import { RegistersTable } from '../registersTable';
import { CargoForm } from './cargoForm';
import { CargoTypeForm } from './cargoTypeForm';
import { columns } from './columns';
import { cargoTypeColumns } from './cargoTypeColumns';
import { productGroupColumns } from './productGroupColumns';
import { GroupForm } from './groupForm';

const { TabPane: AntTabPane } = Tabs;

type FormToViewType = 'CARGO' | 'CARGO_TYPE' | 'PRODUCT_GROUP' | null;

const PAGE_SIZE = 10;

export function Cargo() {
  const { onChangePagination, queryPage, setQueryPage } = useChangePagination();
  const [searchQueryTerm, setSearchQueryTerm] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<string>('CARGO');
  const [selectedCargo, setSelectedCargo] = useState<CargoType>(
    {} as CargoType
  );
  const [selectedCargoType, setSelectedCargoType] =
    useState<CargoTypeType | null>(null);
  const [selectedProductGroup, setSelectedProductGroup] =
    useState<ProductGroup | null>(null);

  const [searchCargoTypeTerm, setSearchCargoTypeTerm] = useState('');
  const [searchProductGroupTerm, setSearchProductGroupTerm] = useState('');
  const [queryPageCargoType, setQueryPageCargoType] = useState(1);
  const [queryPageProductGroup, setQueryPageProductGroup] = useState(1);

  const {
    data: cargoData,
    isFetching: isFetchingCargoData,
    refetch: refetchCargoData,
  } = useGetCargosQuery({
    page: onChangePagination(),
    name: searchQueryTerm,
    page_size: PAGE_SIZE,
  });

  const { data: cargoTypeData, isFetching: isFetchingCargoTypeData } =
    useGetCargoTypeQuery({
      page: queryPageCargoType,
      page_size: PAGE_SIZE,
      name: searchCargoTypeTerm || undefined,
    });
  const { data: productGroupData, isFetching: isFetchingProductGroupData } =
    useGetProductGroupsQuery({
      page: queryPageProductGroup,
      page_size: PAGE_SIZE,
      name: searchProductGroupTerm || undefined,
    });

  const [formToView, setFormToView] = useState<FormToViewType>(null);
  const [exportRegisters, { isLoading: isExporting }] =
    useLazyExportRegistersQuery();

  async function handleExport(
    exportType: 'cargos' | 'cargo_types' | 'product_groups',
    searchTerm: string
  ) {
    try {
      await exportRegisters({
        exportType,
        params: { search: searchTerm || undefined },
      }).unwrap();
      message.success(
        'Exportação em processamento. Você a receberá em seu e-mail em breve.',
        5
      );
    } catch (error) {
      message.error('Erro ao solicitar a exportação. Tente novamente.');
    }
  }

  function onSaveCargo(savedCargo: CargoType) {
    if (savedCargo) refetchCargoData();
  }

  function onChangePaginationCargoType(page: number) {
    setQueryPageCargoType(page);
    return page;
  }

  function onChangePaginationProductGroup(page: number) {
    setQueryPageProductGroup(page);
    return page;
  }

  function onSearchCargoType(term: string) {
    setSearchCargoTypeTerm(term);
    setQueryPageCargoType(1);
  }

  function onSearchProductGroup(term: string) {
    setSearchProductGroupTerm(term);
    setQueryPageProductGroup(1);
  }

  function onSelectCargo(cargo: Record<string, any>) {
    setFormToView('CARGO');
    setSelectedCargo({
      name: cargo.name,
      density_min: cargo.density_min,
      density_max: cargo.density_max,
      cargo_type: cargo.cargo_type.name,
      id: cargo.id,
      product_group: cargo.cargo_type.product_group.id,
      cargo_nature: cargo.cargo_type.product_group.nature_of_cargo.id,
      ncm: cargo.ncm,
    });
  }

  function onSelectCargoType(record: CargoTypeType) {
    setSelectedCargoType(record);
    setFormToView('CARGO_TYPE');
  }

  function onSelectProductGroup(record: ProductGroup) {
    setSelectedProductGroup(record);
    setFormToView('PRODUCT_GROUP');
  }

  function onCloseForm() {
    setFormToView(null);
    setSelectedCargo({} as CargoType);
    setSelectedCargoType(null);
    setSelectedProductGroup(null);
  }

  function openNewCargoType() {
    setSelectedCargoType(null);
    setFormToView('CARGO_TYPE');
  }

  function openNewProductGroup() {
    setSelectedProductGroup(null);
    setFormToView('PRODUCT_GROUP');
  }

  function actionButtons() {
    return (
      <Space size={18}>
        <Button
          icon={<DownloadOutlined />}
          size="large"
          onClick={() => handleExport('cargos', searchQueryTerm)}
          loading={isExporting}
        >
          Exportar XLS
        </Button>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          size="large"
          onClick={openNewProductGroup}
        >
          Novo grupo
        </Button>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          size="large"
          onClick={openNewCargoType}
        >
          Novo tipo
        </Button>
        <Button
          icon={<PlusOutlined />}
          type="primary"
          size="large"
          onClick={() => {
            setSelectedCargo({} as CargoType);
            setFormToView('CARGO');
          }}
        >
          Nova mercadoria
        </Button>
      </Space>
    );
  }

  const cargoTypeList = cargoTypeData?.results || [];
  const productGroupList = productGroupData?.results || [];

  return (
    <>
      <RegistersDrawer visible={formToView !== null} width={640}>
        {formToView === 'CARGO' && (
          <CargoForm
            onClose={onCloseForm}
            onSaveCargo={onSaveCargo}
            selectedCargo={selectedCargo}
            setQueryPage={setQueryPage}
            refetchCargoData={refetchCargoData}
          />
        )}
        {formToView === 'CARGO_TYPE' && (
          <CargoTypeForm
            onClose={onCloseForm}
            selectedCargoType={selectedCargoType ?? undefined}
          />
        )}
        {formToView === 'PRODUCT_GROUP' && (
          <GroupForm
            onClose={onCloseForm}
            selectedProductGroup={selectedProductGroup ?? undefined}
          />
        )}
      </RegistersDrawer>

      <Tabs activeKey={activeSubTab} onChange={setActiveSubTab} size="large">
        <AntTabPane tab="Mercadorias" key="CARGO">
          <PanelHeader
            panelName="mercadoria"
            actionButtons={actionButtons()}
            onSearch={setSearchQueryTerm}
          />
          <RegistersTable
            columns={(onSelectItem, _onDeleteItem) => columns(onSelectItem)}
            total={cargoData?.count}
            itemsPerPage={PAGE_SIZE}
            onSelectItem={onSelectCargo}
            dataSource={cargoData?.results || []}
            onChangePagination={onChangePagination}
            isLoading={isFetchingCargoData}
            queryPage={queryPage}
          />
        </AntTabPane>
        <AntTabPane tab="Tipos de mercadoria" key="CARGO_TYPE">
          <PanelHeader
            panelName="tipo de mercadoria"
            onSearch={onSearchCargoType}
            actionButtons={
              <Space size={18}>
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={() =>
                    handleExport('cargo_types', searchCargoTypeTerm)
                  }
                  loading={isExporting}
                >
                  Exportar XLS
                </Button>
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  size="large"
                  onClick={openNewCargoType}
                >
                  Novo tipo
                </Button>
              </Space>
            }
          />
          <RegistersTable
            columns={(onSelectItem, _onDeleteItem) =>
              cargoTypeColumns(onSelectCargoType)
            }
            dataSource={cargoTypeList.map((item: any) => ({
              ...item,
              key: item.name || item.description || item.id,
            }))}
            total={cargoTypeData?.count ?? 0}
            itemsPerPage={PAGE_SIZE}
            onSelectItem={onSelectCargoType}
            isLoading={isFetchingCargoTypeData}
            queryPage={queryPageCargoType}
            onChangePagination={onChangePaginationCargoType}
          />
        </AntTabPane>
        <AntTabPane tab="Grupos de produto" key="PRODUCT_GROUP">
          <PanelHeader
            panelName="grupo de produto"
            onSearch={onSearchProductGroup}
            actionButtons={
              <Space size={18}>
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={() =>
                    handleExport('product_groups', searchProductGroupTerm)
                  }
                  loading={isExporting}
                >
                  Exportar XLS
                </Button>
                <Button
                  icon={<PlusOutlined />}
                  type="primary"
                  size="large"
                  onClick={openNewProductGroup}
                >
                  Novo grupo
                </Button>
              </Space>
            }
          />
          <RegistersTable
            columns={(onSelectItem, _onDeleteItem) =>
              productGroupColumns(onSelectProductGroup)
            }
            dataSource={(productGroupList as any[])
              .slice()
              .sort((a: any, b: any) =>
                (a.name || '').localeCompare(b.name || '')
              )
              .map((item: any) => ({
                ...item,
                key: item.id ?? item.name,
              }))}
            total={productGroupData?.count ?? 0}
            itemsPerPage={PAGE_SIZE}
            onSelectItem={onSelectProductGroup}
            isLoading={isFetchingProductGroupData}
            queryPage={queryPageProductGroup}
            onChangePagination={onChangePaginationProductGroup}
          />
        </AntTabPane>
      </Tabs>
    </>
  );
}
